import React, { useState, useEffect, useCallback } from 'react';
import {
    Typography,
    Grid,
    CircularProgress,
    Button,
    Snackbar,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Chip,
    Box,
    Tooltip,
    TextField,
    MenuItem
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { getRoles, assignRole, updateRole, removeRole } from '../services/adminApi';
import AssignRoleDialog from '../components/AssignRoleDialog';
import EditRoleDialog from '../components/EditRoleDialog';

const DirectorManagement = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Dialog states
    const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    const [filters, setFilters] = useState({
        name: '',
        email: '',
        role: '',
        state_id: '',
        sport_id: '',
        director_id: '',
        sort_by: 'role',
        sort_order: 'DESC'
    });

    const fetchRoles = useCallback(async () => {
        try {
            setLoading(true);
            // Clean up filters
            const params = {};
            Object.keys(filters).forEach(key => {
                if (filters[key]) params[key] = filters[key];
            });

            const response = await getRoles(params);
            setUsers(response.data.users || []);
            setError(null);
        } catch (err) {
            console.error('Failed to fetch roles:', err);
            setError('Failed to fetch roles. Please ensure you are logged in correctly.');
            setSnackbar({ open: true, message: 'Failed to fetch roles', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchRoles();
    }, [fetchRoles]);

    const handleFilterChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleAssignRole = async (data) => {
        try {
            await assignRole(data);
            setIsAssignDialogOpen(false);
            setSnackbar({ open: true, message: 'Role assigned successfully', severity: 'success' });
            fetchRoles();
        } catch (err) {
            console.error('Failed to assign role:', err);
            const errorMessage = err.response?.data?.message || 'Failed to assign role';
            setSnackbar({ open: true, message: errorMessage, severity: 'error' });
        }
    };

    const handleUpdateRole = async (data) => {
        try {
            await updateRole(data);
            setIsEditDialogOpen(false);
            setSnackbar({ open: true, message: 'Role updated successfully', severity: 'success' });
            fetchRoles();
        } catch (err) {
            console.error('Failed to update role:', err);
            const errorMessage = err.response?.data?.message || 'Failed to update role';
            setSnackbar({ open: true, message: errorMessage, severity: 'error' });
        }
    };

    const handleRemoveRole = async (userId) => {
        if (!window.confirm('Are you sure you want to remove this role? This will revoke access.')) {
            return;
        }
        try {
            await removeRole(userId);
            setSnackbar({ open: true, message: 'Role removed successfully', severity: 'success' });
            fetchRoles();
        } catch (err) {
            console.error('Failed to remove role:', err);
            setSnackbar({ open: true, message: 'Failed to remove role', severity: 'error' });
        }
    };

    const openEditDialog = (user) => {
        setSelectedUser(user);
        setIsEditDialogOpen(true);
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const getRoleColor = (role) => {
        switch (role) {
            case 'national_director': return 'secondary';
            case 'state_director': return 'primary';
            case 'director': return 'info';
            case 'staff': return 'default';
            default: return 'default';
        }
    };

    return (
        <div>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">Director Management</Typography>
                <Button
                    startIcon={<RefreshIcon />}
                    onClick={fetchRoles}
                >
                    Refresh
                </Button>
            </Box>

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            label="Name"
                            name="name"
                            value={filters.name}
                            onChange={handleFilterChange}
                            fullWidth
                            size="small"
                            placeholder="Search name..."
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            label="Email"
                            name="email"
                            value={filters.email}
                            onChange={handleFilterChange}
                            fullWidth
                            size="small"
                            placeholder="Search email..."
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            label="Director ID"
                            name="director_id"
                            value={filters.director_id}
                            onChange={handleFilterChange}
                            fullWidth
                            size="small"
                            type="number"
                        />
                    </Grid>
                    <Grid item xs={6} md={1}>
                        <TextField
                            label="State ID"
                            name="state_id"
                            value={filters.state_id}
                            onChange={handleFilterChange}
                            fullWidth
                            size="small"
                            type="number"
                        />
                    </Grid>
                    <Grid item xs={6} md={1}>
                        <TextField
                            label="Sport ID"
                            name="sport_id"
                            value={filters.sport_id}
                            onChange={handleFilterChange}
                            fullWidth
                            size="small"
                            type="number"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            select
                            label="Role"
                            name="role"
                            value={filters.role}
                            onChange={handleFilterChange}
                            fullWidth
                            size="small"
                        >
                            <MenuItem value="">All Roles</MenuItem>
                            <MenuItem value="national_director">National Director</MenuItem>
                            <MenuItem value="state_director">State Director</MenuItem>
                            <MenuItem value="director">Director</MenuItem>
                            <MenuItem value="staff">Staff</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <Box display="flex" gap={1}>
                            <TextField
                                select
                                label="Sort By"
                                name="sort_by"
                                value={filters.sort_by}
                                onChange={handleFilterChange}
                                fullWidth
                                size="small"
                            >
                                <MenuItem value="role">Role</MenuItem>
                                <MenuItem value="email">Email</MenuItem>
                                <MenuItem value="name">Name</MenuItem>
                                <MenuItem value="created_at">Date</MenuItem>
                            </TextField>
                            <TextField
                                select
                                name="sort_order"
                                value={filters.sort_order}
                                onChange={handleFilterChange}
                                size="small"
                                sx={{ width: 80 }}
                            >
                                <MenuItem value="ASC">ASC</MenuItem>
                                <MenuItem value="DESC">DESC</MenuItem>
                            </TextField>
                        </Box>
                    </Grid>

                </Grid>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
            )}

            {loading ? (
                <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Role</TableCell>
                                <TableCell>Director ID</TableCell>
                                <TableCell>States</TableCell>
                                <TableCell>Sports</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">No users found</TableCell>
                                </TableRow>
                            ) : (
                                users.map((user) => {
                                    const roleData = user.metadata?.role_data || {};
                                    return (
                                        <TableRow key={user.id}>
                                            <TableCell>
                                                <Typography variant="body1">{user.first_name} {user.last_name}</Typography>
                                                {user.nickname && <Typography variant="caption" color="textSecondary">({user.nickname})</Typography>}
                                            </TableCell>
                                            <TableCell>{user.email}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={user.role?.replace('_', ' ').toUpperCase()}
                                                    color={getRoleColor(user.role)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>{roleData.director_id || '-'}</TableCell>
                                            <TableCell>
                                                {roleData.state_ids?.length > 0
                                                    ? roleData.state_ids.join(', ')
                                                    : '-'}
                                            </TableCell>
                                            <TableCell>
                                                {roleData.sport_ids?.length > 0
                                                    ? roleData.sport_ids.join(', ')
                                                    : '-'}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Box display="flex" justifyContent="flex-end" gap={1}>
                                                    <Tooltip title="Edit">
                                                        <IconButton onClick={() => openEditDialog(user)} size="small">
                                                            <EditIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Remove Role">
                                                        <IconButton onClick={() => handleRemoveRole(user.id)} color="error" size="small">
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <AssignRoleDialog
                open={isAssignDialogOpen}
                onClose={() => setIsAssignDialogOpen(false)}
                onSubmit={handleAssignRole}
            />

            <EditRoleDialog
                open={isEditDialogOpen}
                onClose={() => setIsEditDialogOpen(false)}
                onSubmit={handleUpdateRole}
                user={selectedUser}
            />

            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleSnackbarClose}>
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default DirectorManagement;
